import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Image, Upload, X, ArrowLeft } from 'lucide-react';
import { apiFetch } from '../../utils/api';
import styles from './PatientPhoto.module.css';

const API_BASE_URL = `${process.env.REACT_APP_CLIENT_BASE_URL}/api/patients`;

const PatientPhoto = () => {
    const { patientId } = useParams();
    const navigate = useNavigate();
    const [patient, setPatient] = useState(null);
    const [photos, setPhotos] = useState([]);
    const [loading, setLoading] = useState(true);
    const [uploading, setUploading] = useState(false);
    const [error, setError] = useState(null);
    const [draggedIndex, setDraggedIndex] = useState(null);
    const [selectedImage, setSelectedImage] = useState(null);

    const fetchPatientAndPhotos = async () => {
        try {
            // Fetch patient data
            const patientResponse = await apiFetch(`${API_BASE_URL}/${patientId}`);
            if (!patientResponse.ok) throw new Error('Failed to fetch patient');
            const patientData = await patientResponse.json();
            setPatient(patientData);

            // Fetch patient photos
            const photosResponse = await apiFetch(`${API_BASE_URL}/fori/${patientId}`);
            if (!photosResponse.ok) throw new Error('Failed to fetch photos');
            const photosData = await photosResponse.json();
            setPhotos(photosData.images || []);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };


    const handleDragStart = (e, index) => {
        setDraggedIndex(index);
        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setData('text/plain', index);
    };

    const handleDragOver = (e, index) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
    };

    const handleDrop = async (e, dropIndex) => {
        e.preventDefault();
        if (draggedIndex === null || draggedIndex === dropIndex) return;

        const newPhotos = [...photos];
        const draggedPhoto = newPhotos[draggedIndex];
        newPhotos.splice(draggedIndex, 1);
        newPhotos.splice(dropIndex, 0, draggedPhoto);

        setPhotos(newPhotos);
        setDraggedIndex(null);

        try {
            const orderedIds = newPhotos.map(photo => photo._id);
            const response = await apiFetch(`${API_BASE_URL}/fori/${patientId}/reorder`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ orderedIds })
            });
            if (!response.ok) throw new Error('Failed to reorder images');
        } catch (err) {
            console.error(err);
            setError('Failed to save new photo order');
            fetchPatientAndPhotos();
        }
    };

    useEffect(() => {
        fetchPatientAndPhotos();
    }, [patientId]);

    const handleFileUpload = async (event) => {
        const files = Array.from(event.target.files);

        if (files.length === 0) return;
        if (files.length + photos.length > 12) {
            setError('Maximum 12 photos allowed');
            return;
        }

        // Check file sizes on frontend (15MB limit)
        const maxFileSize = 15 * 1024 * 1024; // 15MB
        const oversizedFiles = files.filter(file => file.size > maxFileSize);

        if (oversizedFiles.length > 0) {
            const oversizedNames = oversizedFiles
                .map(f => `${f.name} (${(f.size / 1024 / 1024).toFixed(2)}MB)`)
                .join(', ');
            setError(`File size exceeds 15MB limit. Please optimize your images. Oversized: ${oversizedNames}`);
            return;
        }

        setUploading(true);
        setError(null);

        const formData = new FormData();
        files.forEach(file => {
            const safeName = (file.name || 'image.jpg').replace(/[^a-zA-Z0-9.\-_]/g, '_');
            formData.append('images', file, safeName);
        });

        try {
            const response = await apiFetch(`${API_BASE_URL}/fori/${patientId}`, {
                method: 'POST',
                body: formData,
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || 'Failed to upload images');
            }

            const successData = await response.json();
            console.log('[Image Upload Success]', successData);

            await fetchPatientAndPhotos(); // Refresh images after upload
        } catch (err) {
            console.error('[Image Upload Error]', err);
            setError(err.message);
        } finally {
            setUploading(false);
        }
    };

    const handleDeleteImage = async (imageId) => {
        if (!window.confirm('Are you sure you want to delete this image?')) return;

        try {
            const response = await apiFetch(`${API_BASE_URL}/fori/${patientId}/${imageId}`, {
                method: 'DELETE',
            });

            if (!response.ok) throw new Error('Failed to delete image');

            await fetchPatientAndPhotos(); // Refresh images after deletion
        } catch (err) {
            setError(err.message);
        }
    };

    return (
        <div className="container py-4">
            <div className="card shadow-sm">
                <div className="card-header bg-white">
                    <div className="d-flex justify-content-between align-items-center">
                        <div className="d-flex align-items-center">
                            <button
                                className="btn btn-link text-secondary p-0 me-3"
                                onClick={() => navigate(`/patients/${patientId}`)}
                            >
                                <ArrowLeft size={20} />
                            </button>
                            <div>
                                <h5 className="card-title mb-1">Patient Photos</h5>
                                <p className="text-muted mb-0 small">
                                    Upload and manage patient photos ({photos.length}/12)
                                </p>
                            </div>
                        </div>
                        <div>
                            <input
                                type="file"
                                id="photo-upload"
                                multiple
                                accept="image/*"
                                className="d-none"
                                onChange={handleFileUpload}
                                disabled={uploading || photos.length >= 12}
                            />
                            <label
                                htmlFor="photo-upload"
                                className={`btn d-flex align-items-center gap-2 ${
                                    uploading || photos.length >= 12 
                                        ? 'btn-secondary disabled' 
                                        : 'btn-primary'
                                }`}
                                style={{ 
                                    cursor: (uploading || photos.length >= 12) ? 'not-allowed' : 'pointer' 
                                }}
                            >
                                <Upload size={16} />
                                {uploading ? 'Uploading...' : 
                                 photos.length >= 12 ? 'Limit Reached' : 'Add Photos'}
                            </label>
                        </div>
                    </div>
                </div>
                <div className="card-body">
                    {error && (
                        <div className="alert alert-danger">{error}</div>
                    )}

                    {loading ? (
                        <div className="text-center py-4">Loading images...</div>
                    ) : photos.length === 0 ? (
                        <div className="text-center py-4 text-muted">
                            <Image size={48} className="mb-3 opacity-50" />
                            <p>No photos uploaded yet</p>
                        </div>
                    ) : (
                        <div className={styles.photoGrid}>
                            {photos.map((image, index) => (
                                <div 
                                      key={image._id} 
                                      className={styles.photoContainer}
                                      draggable
                                      onDragStart={(e) => handleDragStart(e, index)}
                                      onDragOver={(e) => handleDragOver(e, index)}
                                      onDrop={(e) => handleDrop(e, index)}
                                      onDragEnd={() => setDraggedIndex(null)}
                                      style={{
                                          opacity: draggedIndex === index ? 0.5 : 1,
                                          cursor: 'grab',
                                          border: draggedIndex === index ? '2px dashed #007bff' : 'none'
                                      }}
                                  >
                                    <img
                                          src={`${process.env.REACT_APP_CLIENT_BASE_URL}/api/patients/image/${image._id}?token=${localStorage.getItem('token')}`}
                                          alt={`Patient photo ${index + 1}`}
                                          className={styles.photo}
                                          onClick={() => setSelectedImage(image)}
                                          style={{ cursor: 'pointer' }}
                                      />
                                    <div className={styles.imageInfo}>
                                        <span className={styles.uploadDate}>
                                            {new Date(image.uploadedAt).toLocaleDateString()}
                                        </span>
                                        <button
                                            className={styles.deleteButton}
                                            onClick={() => handleDeleteImage(image._id)}
                                            title="Delete photo"
                                        >
                                            <X size={14} />
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {selectedImage && (
                <div 
                    style={{
                        position: 'fixed',
                        top: 0, left: 0, right: 0, bottom: 0,
                        backgroundColor: 'rgba(0,0,0,0.85)',
                        zIndex: 9999,
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center'
                    }}
                    onClick={() => setSelectedImage(null)}
                >
                    <button 
                        style={{
                            position: 'absolute',
                            top: '20px', right: '20px',
                            background: 'white',
                            border: 'none',
                            borderRadius: '50%',
                            width: '40px', height: '40px',
                            cursor: 'pointer',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            boxShadow: '0 2px 10px rgba(0,0,0,0.2)'
                        }}
                        onClick={() => setSelectedImage(null)}
                    >
                        <X size={24} color="black" />
                    </button>
                    <img 
                        src={`${process.env.REACT_APP_CLIENT_BASE_URL}/api/patients/image/${selectedImage._id}?token=${localStorage.getItem('token')}`} 
                        alt="Enlarged patient photo" 
                        style={{ 
                            maxWidth: '90vw', 
                            maxHeight: '90vh', 
                            objectFit: 'contain',
                            borderRadius: '8px',
                            boxShadow: '0 5px 25px rgba(0,0,0,0.5)'
                        }}
                        onClick={(e) => e.stopPropagation()}
                    />
                </div>
            )}
        </div>
    );
};

export default PatientPhoto;